// Аватар-ассистент проводника: синхронизация реплик, live-диалог, clarify-вопросы,
// голосовой ввод. Хост (экран/шаг/переход к визарду) подключается late-bound через
// configureAssistantHost — граф модулей остаётся ацикличным.
// Выделено из onboarding.ts (аудит, этап 2 — декомпозиция).
import {
  getOnboardingRuntimeConfig,
  isAvatarScriptExperience,
  isAvatarLiveExperience,
} from './onboarding-presenter';
import {
  findReplicaForScreen,
  setAvatarReplicaText,
  setAvatarVideoSrc,
  showClarifyAnswer,
  updateClarifyHint,
} from './onboarding-avatar-presenter';
import {
  getLiveDialogLimits,
  setLiveMicListening,
  showLiveDialogAnswer,
  updateLiveDialogHint,
} from './onboarding-live-presenter';
import { getOnboardingSessionId } from './onboarding-session';
import { WIZARD_STEPS, type OnboardingScreen } from './onboarding-flow-data';

let clarifyCount = 0;
let dialogTurnCount = 0;

type LiveSpeechRecognition = {
  lang: string;
  interimResults: boolean;
  continuous: boolean;
  start(): void;
  stop(): void;
  onresult: ((event: { results: { [i: number]: { [j: number]: { transcript: string } } } }) => void) | null;
  onerror: (() => void) | null;
  onend: (() => void) | null;
};
let liveRecognition: LiveSpeechRecognition | null = null;

type AssistantHost = {
  getScreen(): OnboardingScreen;
  getStepIndex(): number;
  renderWizard(): void;
};

let host: AssistantHost | null = null;

/** Подключение хоста (onboarding.ts) на module evaluation — до первого рендера. */
export function configureAssistantHost(next: AssistantHost) {
  host = next;
}

export function syncAvatarReplica(screen: OnboardingScreen) {
  if (isAvatarLiveExperience()) {
    const { maxTurns } = getLiveDialogLimits();
    updateLiveDialogHint(dialogTurnCount, maxTurns);
    const stepKey = screen === 'wizard' ? WIZARD_STEPS[host?.getStepIndex() ?? 0]?.key : undefined;
    const replica = findReplicaForScreen(screen, stepKey);
    if (replica) {
      setAvatarReplicaText(replica.text);
    }
    return;
  }

  if (!isAvatarScriptExperience()) return;

  const cfg = getOnboardingRuntimeConfig().avatar;
  const maxClarify = cfg?.max_clarify ?? 2;
  updateClarifyHint(clarifyCount, maxClarify);

  const stepKey = screen === 'wizard' ? WIZARD_STEPS[host?.getStepIndex() ?? 0]?.key : undefined;
  const replica = findReplicaForScreen(screen, stepKey);
  if (replica) {
    setAvatarReplicaText(replica.text);
    const clipUrl = `${cfg?.base_path || '/assets/onboarding/avatar'}/${replica.id}.mp4`;
    setAvatarVideoSrc(clipUrl);
  }
}

export async function submitLiveDialogQuestion(userText: string, bargeIn = false): Promise<void> {
  const { maxTurns } = getLiveDialogLimits();
  if (dialogTurnCount >= maxTurns) return;

  const stepKey = host?.getScreen() === 'wizard' ? WIZARD_STEPS[host?.getStepIndex() ?? 0]?.key : undefined;

  try {
    const response = await fetch('/api/journey/onboarding/dialog', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: getOnboardingSessionId(),
        user_text: userText,
        step_key: stepKey,
        barge_in: bargeIn,
      }),
    });

    if (!response.ok) return;

    const data = await response.json();
    dialogTurnCount = data.turn_count ?? dialogTurnCount + 1;
    showLiveDialogAnswer(data.answer_text || '');
    updateLiveDialogHint(dialogTurnCount, maxTurns);

    const replicaEl = document.getElementById('onboarding-avatar-replica');
    if (replicaEl) replicaEl.textContent = data.answer_text || '';

    if (data.turn_cap_reached && host?.getScreen() !== 'wizard') {
      host?.renderWizard();
    }
  } catch {
    // live dialog is optional enhancement
  }
}

export function startLiveVoiceInput() {
  const SpeechRec = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
  if (!SpeechRec) return;

  if (liveRecognition) {
    liveRecognition.stop();
    liveRecognition = null;
    setLiveMicListening(false);
    return;
  }

  const rec = new SpeechRec() as LiveSpeechRecognition;
  rec.lang = 'ru-RU';
  rec.interimResults = false;
  rec.continuous = false;
  liveRecognition = rec;
  setLiveMicListening(true);

  rec.onresult = (event) => {
    const transcript = event.results[0]?.[0]?.transcript?.trim() || '';
    if (transcript) void submitLiveDialogQuestion(transcript);
  };

  rec.onerror = () => {
    setLiveMicListening(false);
    liveRecognition = null;
  };

  rec.onend = () => {
    setLiveMicListening(false);
    liveRecognition = null;
  };

  rec.start();
}

export async function submitClarifyQuestion(userText: string): Promise<void> {
  const cfg = getOnboardingRuntimeConfig().avatar;
  const maxClarify = cfg?.max_clarify ?? 2;
  if (clarifyCount >= maxClarify) return;

  const stepKey = host?.getScreen() === 'wizard' ? WIZARD_STEPS[host?.getStepIndex() ?? 0]?.key : undefined;

  try {
    const response = await fetch('/api/journey/onboarding/llm', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: getOnboardingSessionId(),
        user_text: userText,
        step_key: stepKey,
        clarify_count: clarifyCount,
      }),
    });

    if (!response.ok) return;

    const data = await response.json();
    clarifyCount = data.clarify_count ?? clarifyCount + 1;
    showClarifyAnswer(data.answer_text || '');
    updateClarifyHint(clarifyCount, maxClarify);

    if (data.avatar?.video_url) {
      setAvatarVideoSrc(data.avatar.video_url);
    } else {
      setAvatarReplicaText(data.answer_text || '');
    }

    if (data.action === 'skip_to_wizard' && host?.getScreen() !== 'wizard') {
      host?.renderWizard();
    }
  } catch {
    // clarify is optional enhancement; wizard remains usable
  }
}
